import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DownloadIcon from "@mui/icons-material/Download";
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridSortModel
} from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";

type LogEntryResponse = {
  id?: number;
  timestamp?: string;
  level?: string;
  user?: string;
  message?: string;
  attributes?: Record<string, unknown> | null;
  raw?: string;
};

type LogEntryRow = {
  id: number;
  timestamp: string;
  level: string;
  user: string;
  message: string;
  attributes: Record<string, string>;
  raw: string;
};

type LogPageResponse = {
  entries?: LogEntryResponse[];
  page?: number;
  pageSize?: number;
  totalEntries?: number;
  totalPages?: number;
};

type LogPageResult = {
  entries: LogEntryRow[];
  page: number;
  pageSize: number;
  totalEntries: number;
  totalPages: number;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200] as const;
const LOGS_PAGE_SIZE_STORAGE_KEY = "logs-table-page-size";

const CHIP_WIDTH = 80;
const DEFAULT_LOG_USER = "System";
const LOGS_DOWNLOAD_PAGE_SIZE = 500;

const getStoredPageSize = () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  const storedValue = window.localStorage.getItem(LOGS_PAGE_SIZE_STORAGE_KEY);
  const parsedValue = storedValue ? Number.parseInt(storedValue, 10) : NaN;
  if (Number.isNaN(parsedValue)) {
    return undefined;
  }
  const options = Array.from(PAGE_SIZE_OPTIONS);
  return options.includes(parsedValue as (typeof PAGE_SIZE_OPTIONS)[number])
    ? (parsedValue as (typeof PAGE_SIZE_OPTIONS)[number])
    : undefined;
};

const persistPageSize = (pageSize: number) => {
  if (typeof window === "undefined") {
    return;
  }
  const options = Array.from(PAGE_SIZE_OPTIONS);
  if (options.includes(pageSize as (typeof PAGE_SIZE_OPTIONS)[number])) {
    window.localStorage.setItem(LOGS_PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }
};

const escapeCsv = (value: string) => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, "\"\"")}"`;
  }
  return value;
};

const downloadCsv = (fileName: string, csv: string) => {
  if (typeof window === "undefined") {
    return;
  }
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const LogsTable = () => {
  const { authorizedFetch } = useAuth();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(() => {
    const storedPageSize = getStoredPageSize();
    return {
      page: 0,
      pageSize: storedPageSize ?? 50
    };
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "timestamp", sort: "desc" }
  ]);
  const [searchText, setSearchText] = useState("");
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const normalizedSearch = useMemo(() => searchText.trim(), [searchText]);

  const sortField = useMemo(() => sortModel[0]?.field ?? "timestamp", [sortModel]);
  const sortOrder = useMemo(() => sortModel[0]?.sort ?? "desc", [sortModel]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch
  } = useQuery<LogPageResult>({
    queryKey: [
      "logs",
      paginationModel.page,
      paginationModel.pageSize,
      sortField,
      sortOrder,
      normalizedSearch.toLowerCase()
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(paginationModel.page + 1),
        pageSize: String(paginationModel.pageSize)
      });
      if (sortField === "timestamp" || sortField === "level" || sortField === "message") {
        params.set("sortBy", sortField);
        params.set("order", sortOrder);
      }
      if (normalizedSearch) {
        params.set("query", normalizedSearch);
      }
      const url = `/api/logs?${params.toString()}`;
      const response = await authorizedFetch(url);
      const payload = (await response.json().catch(() => null)) as (LogPageResponse & { error?: string }) | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load logs");
      }

      const page = typeof payload?.page === "number" && payload.page > 0 ? payload.page : 1;
      const pageSize =
        typeof payload?.pageSize === "number" && payload.pageSize > 0 ? payload.pageSize : paginationModel.pageSize;

      const entries = Array.isArray(payload?.entries) ? payload.entries : [];
      const sanitisedEntries: LogEntryRow[] = entries.map((entry, index) => {
        const attributes =
          entry?.attributes && typeof entry.attributes === "object"
            ? Object.fromEntries(
                Object.entries(entry.attributes).map(([key, value]) => [String(key), String(value)])
              )
            : {};
        if (attributes.user) {
          delete attributes.user;
        }
        const userValue = typeof entry?.user === "string" ? entry.user.trim() : "";
        return {
          id:
            typeof entry?.id === "number" && !Number.isNaN(entry.id)
              ? entry.id
              : (page - 1) * pageSize + index + 1,
          timestamp: typeof entry?.timestamp === "string" ? entry.timestamp : "",
          level: typeof entry?.level === "string" ? entry.level : "",
          user: userValue || DEFAULT_LOG_USER,
          message: typeof entry?.message === "string" ? entry.message : "",
          raw: typeof entry?.raw === "string" ? entry.raw : "",
          attributes
        };
      });

      const totalEntries =
        typeof payload?.totalEntries === "number" && payload.totalEntries >= 0
          ? payload.totalEntries
          : sanitisedEntries.length;
      const totalPages =
        typeof payload?.totalPages === "number" && payload.totalPages >= 0 ? payload.totalPages : 0;

      return {
        entries: sanitisedEntries,
        page,
        pageSize,
        totalEntries,
        totalPages
      };
    },
    placeholderData: (previousData) => previousData
  });

  const handleOpenClearDialog = useCallback(() => {
    setClearError("");
    setIsClearDialogOpen(true);
  }, []);

  const handleCloseClearDialog = useCallback(() => {
    if (isClearing) {
      return;
    }
    setIsClearDialogOpen(false);
  }, [isClearing]);

  const handleConfirmClear = useCallback(async () => {
    setClearError("");
    setIsClearing(true);
    try {
      const response = await authorizedFetch("/api/logs", { method: "DELETE" });
      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to clear logs");
      }
      setIsClearDialogOpen(false);
      await refetch();
    } catch (caughtError) {
      if (caughtError instanceof Error) {
        setClearError(caughtError.message);
        console.error("[LogsTable] clear logs failed:", caughtError);
      } else if (typeof caughtError === "string") {
        setClearError(caughtError);
        console.error("[LogsTable] clear logs failed:", caughtError);
      } else {
        setClearError("Unable to clear logs");
        console.error("[LogsTable] clear logs failed with unknown error:", caughtError);
      }
    } finally {
      setIsClearing(false);
    }
  }, [authorizedFetch, isClearing, refetch]);

  const handleDownloadLogs = useCallback(async () => {
    setDownloadError("");
    setIsDownloading(true);
    try {
      let page = 1;
      let totalPages = 1;
      const allRows: LogEntryRow[] = [];

      while (page <= totalPages) {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(LOGS_DOWNLOAD_PAGE_SIZE),
          sortBy: sortField,
          order: sortOrder
        });
        if (normalizedSearch) {
          params.set("query", normalizedSearch);
        }

        const response = await authorizedFetch(`/api/logs?${params.toString()}`);
        const payload = (await response.json().catch(() => null)) as (LogPageResponse & { error?: string }) | null;
        if (!response.ok) {
          throw new Error(payload?.error ?? "Unable to download logs");
        }

        const serverPage = typeof payload?.page === "number" && payload.page > 0 ? payload.page : page;
        const serverPageSize =
          typeof payload?.pageSize === "number" && payload.pageSize > 0 ? payload.pageSize : LOGS_DOWNLOAD_PAGE_SIZE;
        const entries = Array.isArray(payload?.entries) ? payload.entries : [];
        entries.forEach((entry, index) => {
          const attributes =
            entry?.attributes && typeof entry.attributes === "object"
              ? Object.fromEntries(
                  Object.entries(entry.attributes).map(([key, value]) => [String(key), String(value)])
                )
              : {};
          if (attributes.user) {
            delete attributes.user;
          }
          const userValue = typeof entry?.user === "string" ? entry.user.trim() : "";
          allRows.push({
            id:
              typeof entry?.id === "number" && !Number.isNaN(entry.id)
                ? entry.id
                : (serverPage - 1) * serverPageSize + index + 1,
            timestamp: typeof entry?.timestamp === "string" ? entry.timestamp : "",
            level: typeof entry?.level === "string" ? entry.level : "",
            user: userValue || DEFAULT_LOG_USER,
            message: typeof entry?.message === "string" ? entry.message : "",
            raw: typeof entry?.raw === "string" ? entry.raw : "",
            attributes
          });
        });

        totalPages = typeof payload?.totalPages === "number" && payload.totalPages > 0 ? payload.totalPages : 1;
        page += 1;
      }

      const header = ["id", "timestamp", "level", "user", "message", "raw", "attributes"];
      const lines = allRows.map((row) =>
        [
          String(row.id),
          row.timestamp,
          row.level,
          row.user,
          row.message,
          row.raw,
          JSON.stringify(row.attributes)
        ]
          .map((value) => escapeCsv(value))
          .join(",")
      );
      const csv = [header.join(","), ...lines].join("\n");
      const fileStamp = new Date().toISOString().replace(/[:.]/g, "-");
      downloadCsv(`skyview-logs-${fileStamp}.csv`, csv);
    } catch (caughtError) {
      if (caughtError instanceof Error) {
        setDownloadError(caughtError.message);
      } else if (typeof caughtError === "string") {
        setDownloadError(caughtError);
      } else {
        setDownloadError("Unable to download logs");
      }
    } finally {
      setIsDownloading(false);
    }
  }, [authorizedFetch, normalizedSearch, sortField, sortOrder]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const pageSizes = Array.from(PAGE_SIZE_OPTIONS);
    setPaginationModel((previous) => {
      const serverPage = Math.max(data.page - 1, 0);
      const serverPageSize = data.pageSize > 0 ? data.pageSize : previous.pageSize;
      const normalizedPageSize = pageSizes.includes(serverPageSize as (typeof PAGE_SIZE_OPTIONS)[number])
        ? serverPageSize
        : previous.pageSize;
      if (previous.page === serverPage && previous.pageSize === normalizedPageSize) {
        return previous;
      }
      return {
        page: serverPage,
        pageSize: normalizedPageSize
      };
    });
  }, [data]);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model);
  }, []);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length === 0) {
      return;
    }

    const descriptor = model[0];
    if (!descriptor) {
      return;
    }

    const field = descriptor.field;
    const sort = descriptor.sort;

    if (field !== "timestamp" && field !== "level" && field !== "message") {
      return;
    }

    if (!sort) {
      return;
    }

    setSortModel([{ field, sort }]);
    setPaginationModel((previous) => ({
      page: 0,
      pageSize: previous.pageSize
    }));
  }, []);

  const columns = useMemo<GridColDef<LogEntryRow>[]>(() => {
    const levelChipColor = (level: string): ChipProps["color"] => {
      const upper = level.toUpperCase();
      if (upper === "ERROR") {
        return "error";
      }
      if (upper === "WARN" || upper === "WARNING") {
        return "warning";
      }
      if (upper === "INFO") {
        return "info";
      }
      return "default";
    };

    const toTimestampValue = (value: string) => {
      const parsed = Date.parse(value);
      if (Number.isNaN(parsed)) {
        return null;
      }
      return parsed;
    };

    return [
      {
        field: "timestamp",
        headerName: "Time",
        width: 210,
        minWidth: 210,
        sortable: true,
        resizable: true,
        valueGetter: (value, row) => row?.timestamp ?? "",
        sortComparator: (v1, v2) => {
          const first = toTimestampValue(v1);
          const second = toTimestampValue(v2);
          if (first === null && second === null) {
            return 0;
          }
          if (first === null) {
            return 1;
          }
          if (second === null) {
            return -1;
          }
          return first - second;
        },
        renderCell: (params) => {
          const row = params.row ?? ({} as LogEntryRow);
          const value = row.timestamp?.trim();
          if (!value) {
            return "-";
          }
          const date = new Date(value);
          const isValidDate = !Number.isNaN(date.getTime());
          const displayValue = isValidDate ? date.toLocaleString(undefined, { hourCycle: "h23" }) : value;
          return (
            <Tooltip title={value} placement="top-start" arrow>
              <Typography variant="body2" noWrap>{displayValue}</Typography>
            </Tooltip>
          );
        }
      },
      {
        field: "level",
        headerName: "Level",
        width: 120,
        sortable: true,
        resizable: true,
        renderCell: (params) => {
          const level = (params.row.level ?? "").toUpperCase();
          const color = levelChipColor(level);
          return (
            <Chip
              size="small"
              label={level || "-"}
              color={color}
              variant={color === "default" ? "outlined" : "filled"}
              sx={{ width: CHIP_WIDTH, justifyContent: "center" }}
            />
          );
        }
      },
      {
        field: "user",
        headerName: "User",
        width: 120,
        sortable: false,
        resizable: true,
        valueGetter: (value, row) => row?.user ?? "",
        renderCell: (params) => {
          const row = params.row ?? ({} as LogEntryRow);
          const value = row.user?.trim() || DEFAULT_LOG_USER;
          return (
            <Tooltip title={value} placement="top-start" arrow>
              <Typography variant="body2">{value}</Typography>
            </Tooltip>
          );
        }
      },
      {
        field: "message",
        headerName: "Message",
        flex: 2,
        minWidth: 360,
        sortable: true,
        resizable: true,
        valueGetter: (value, row) => row?.message ?? "",
        renderCell: (params) => {
          const row = params.row ?? ({} as LogEntryRow);
          const attributes = row.attributes ? Object.entries(row.attributes) : [];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5, width: "100%" }}>
              <Tooltip title={row.raw} placement="top-start" arrow enterDelay={300} sx={{ maxWidth: 600 }}>
                <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {row.message || "-"}
                </Typography>
              </Tooltip>
              {attributes.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {attributes.map(([key, value]) => (
                    <Chip
                      key={`${params.row.id}-${key}`}
                      label={`${key}=${value}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        width: CHIP_WIDTH,
                        justifyContent: "flex-start",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        }
      }
    ];
  }, []);

  const rows = data?.entries ?? [];
  const rowCount = data?.totalEntries ?? rows.length;

  useEffect(() => {
    persistPageSize(paginationModel.pageSize);
  }, [paginationModel.pageSize]);

  useEffect(() => {
    setPaginationModel((previous) => (previous.page === 0 ? previous : { ...previous, page: 0 }));
  }, [normalizedSearch]);

  const errorMessage = useMemo(() => {
    if (!isError) {
      return "";
    }
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === "string") {
      return error;
    }
    return "Unable to load logs";
  }, [error, isError]);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={1}
      >
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search logs"
          size="small"
          sx={{ minWidth: { xs: "100%", sm: 240 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
          <Tooltip title="Download logs" arrow>
            <span>
              <IconButton
                aria-label="Download logs"
                color="primary"
                onClick={() => {
                  void handleDownloadLogs();
                }}
                disabled={isFetching || isDownloading}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => {
              void refetch();
            }}
            disabled={isFetching}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleOpenClearDialog}
            disabled={isFetching || isClearing}
          >
            Clear Logs
          </Button>
        </Stack>
      </Stack>
      {isError && <Alert severity="error">{errorMessage}</Alert>}
      {clearError && <Alert severity="error">{clearError}</Alert>}
      {downloadError && <Alert severity="error">{downloadError}</Alert>}
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 500,
          overflow: "hidden"
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          paginationMode="server"
          sortingMode="server"
          sortingOrder={["asc", "desc"]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={Array.from(PAGE_SIZE_OPTIONS)}
          rowCount={rowCount}
          loading={isLoading || isFetching}
          disableRowSelectionOnClick
          disableColumnMenu
          columnResizeMode="onChange"
          getRowHeight={() => "auto"}
          sx={{
            flex: 1,
            border: 0,
            "& .MuiDataGrid-row": {
              maxHeight: "fit-content !important"
            },
            "& .MuiDataGrid-cell": {
              py: 1
            }
          }}
        />
      </Paper>
      <Dialog open={isClearDialogOpen} onClose={handleCloseClearDialog} aria-labelledby="clear-logs-dialog-title">
        <DialogTitle id="clear-logs-dialog-title">Clear logs?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes all current log entries from the server. New log messages will continue to appear after
            clearing. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClearDialog} disabled={isClearing}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleConfirmClear();
            }}
            color="error"
            disabled={isClearing}
            autoFocus
          >
            {isClearing ? "Clearing..." : "Clear logs"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogsTable;
